import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import "./css/CommunityLanding.css";

const GET_APP_URL = "/get-app";

const PAGE_CONFIG = {
  organizer: {
    pageUrl: "https://vaultgrid.net/for-organizers",
  },
  participant: {
    pageUrl: "https://vaultgrid.net/for-participants",
  },
};

const getCommunityContent = (t, variant, language) => {
  const config = PAGE_CONFIG[variant];
  const prefix = `community_pages.${variant}`;
  const shared = "community_pages.shared";

  return {
    ...config,
    pageUrl: `${config.pageUrl}/${language}`,
    title: t(`${prefix}.meta_title`),
    description: t(`${prefix}.meta_description`),
    heroTitle: t(`${shared}.hero_title`, { returnObjects: true }),
    heroLines: t(`${prefix}.hero_lines`, { returnObjects: true }),
    introTitle: t(`${prefix}.intro_title`, { returnObjects: true }),
    introBody: t(`${prefix}.intro_body`),
    benefits: t(`${prefix}.benefits`, { returnObjects: true }),
    footerTitle: t(`${prefix}.footer_title`),
    footerCtaLabel: t(`${prefix}.footer_cta`, { defaultValue: t(`${shared}.cta`) }),
    footerAccent: t(`${shared}.footer_accent`),
    desktopCtaLabel: t(`${shared}.cta`),
    mobileCtaLabel: t(`${shared}.download_app`),
    statsTitle: t(`${shared}.stats_title`, { returnObjects: true }),
    stats: t(`${shared}.stats`, { returnObjects: true }),
    learnMore: t(`${shared}.learn_more`),
    links: t(`${shared}.links`, { returnObjects: true }),
    copyright: t(`${shared}.copyright`),
    guided: variant === "participant"
      ? t(`${prefix}.guided`, { returnObjects: true })
      : null,
  };
};

function Brand({ variant, className = "", footer = false }) {
  const logoSrc = footer && variant === "organizer"
    ? "/assets/landing/organizer/logo.svg"
    : `/assets/one-pagers/participants/participant-logo-${footer ? "footer" : "top"}.svg`;

  return (
    <a className={`vg-community-landing__brand ${className}`} href="/" aria-label="VaultGrid home">
      <img src={logoSrc} alt="VaultGrid" />
    </a>
  );
}

function ActionButton({ href, children, className = "" }) {
  return (
    <a className={`vg-community-landing__action ${className}`} href={href}>
      {children}
    </a>
  );
}

function ResponsiveActionButton({ desktopHref, desktopLabel, mobileHref, mobileLabel, className = "" }) {
  return (
    <>
      <ActionButton href={desktopHref} className={`${className} vg-community-landing__desktop-only`}>
        {desktopLabel}
      </ActionButton>
      <ActionButton href={mobileHref} className={`${className} vg-community-landing__mobile-only`}>
        {mobileLabel}
      </ActionButton>
    </>
  );
}

function PhoneMockup() {
  return (
    <div className="vg-community-landing__phone" aria-hidden="true">
      <img className="vg-community-landing__phone-composite" src="/assets/one-pagers/iphone2.png" alt="" />
    </div>
  );
}

function StatsSection({ title, items }) {
  return (
    <section className="vg-community-landing__stats" aria-labelledby="vg-community-stats-title">
      <h2 id="vg-community-stats-title" className="vg-community-landing__stats-title">
        <span>{title[0]}</span> <em>{title[1]}</em>
      </h2>
      <div className="vg-community-landing__stats-grid">
        {items.map(({ value, label }) => (
          <div className="vg-community-landing__stat" key={label}>
            <strong>
              {value.replace("+", "")}<em>+</em>
            </strong>
            <span>{label}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

function BenefitRow({ index, title, body }) {
  const paragraphs = Array.isArray(body) ? body : [body];
  const titleLines = String(title || "").split("\n");

  return (
    <article className="vg-community-landing__benefit">
      <div className="vg-community-landing__benefit-heading">
        <span className="vg-community-landing__benefit-number">{String(index).padStart(2, "0")}</span>
        <h3>
          {titleLines.map((line) => (
            <span key={line}>{line}</span>
          ))}
        </h3>
      </div>
      <div className="vg-community-landing__benefit-copy">
        {paragraphs.map((paragraph) => (
          <p key={paragraph}>{paragraph}</p>
        ))}
      </div>
    </article>
  );
}

function GuidedSection({ desktopHref, mobileHref, copy }) {
  return (
    <section className="vg-community-landing__guided" aria-label="Newcomer meetup">
      <div className="vg-community-landing__guided-copy">
        <span>{copy.prompt_line_1}</span>
        <span>{copy.prompt_line_2} <b>{copy.prompt_pill}</b>{copy.prompt_suffix}</span>
      </div>
      <span className="vg-community-landing__guided-arrow" aria-hidden="true">
        <picture>
          <source media="(max-width: 640px)" srcSet="/assets/one-pagers/bended_arrow.svg" />
          <img src="/assets/one-pagers/short_arrow.svg" alt="" />
        </picture>
      </span>
      <a className="vg-community-landing__guided-join vg-community-landing__desktop-only" href={desktopHref}>
        <span><b>{copy.join_pill}</b> {copy.join_line_1}</span>
        <span>{copy.join_line_2}</span>
      </a>
      <a className="vg-community-landing__guided-join vg-community-landing__mobile-only" href={mobileHref}>
        <span><b>{copy.join_pill}</b> {copy.join_line_1}</span>
        <span>{copy.join_line_2}</span>
      </a>
    </section>
  );
}

function Footer({ variant, content, href, ctaLabel, mobileHref, mobileCtaLabel }) {
  return (
    <footer className="vg-community-landing__footer">
      <div className="vg-community-landing__footer-top">
        <Brand variant={variant} footer />
        {mobileHref ? (
          <ResponsiveActionButton
            desktopHref={href}
            desktopLabel={ctaLabel}
            mobileHref={mobileHref}
            mobileLabel={mobileCtaLabel}
          />
        ) : (
          <ActionButton href={href}>{ctaLabel}</ActionButton>
        )}
      </div>
      <h2 className="vg-community-landing__footer-title">
        <span>{content.footerTitle}</span>
        <em>{content.footerAccent}</em>
      </h2>
      <nav className="vg-community-landing__footer-links" aria-label="Legal links">
        <a href="/legal/terms">{content.links.terms}</a>
        <a href="/legal/privacy">{content.links.privacy}</a>
        <a href="/legal/terms">{content.links.agreement}</a>
        <a href="/legal/cookies">{content.links.cookies}</a>
      </nav>
        <div className="vg-community-landing__footer-bottom">
          <small>© 2026 VaultGrid S.R.O. {content.copyright}</small>
          <div className="vg-community-landing__socials" aria-label="Social links">
            <a href="https://www.facebook.com/people/Vault-Grid/pfbid02rjbhCXqGRQVCz5JPycXPPAttVB3MAcNHRrHmT1ozYWXUyqX7awZLPhbS9Pcvi1VKl/" target="_blank" rel="noreferrer" aria-label="VaultGrid on Facebook">
              <img src="/assets/social/facebook.png" alt="" />
            </a>
            <a href="https://www.instagram.com/vaultgrid_official" target="_blank" rel="noreferrer" aria-label="VaultGrid on Instagram">
              <img src="/assets/social/instagram.png" alt="" />
            </a>
            <a href="https://t.me/vaultgrid" target="_blank" rel="noreferrer" aria-label="VaultGrid on Telegram">
              <img src="/assets/one-pagers/participants/social-telegram.svg" alt="" />
            </a>
          </div>
      </div>
    </footer>
  );
}

export default function CommunityLanding({ variant, language = "en" }) {
  const pageLanguage = ["sk", "cs"].includes(language) ? language : "en";
  const { i18n } = useTranslation("landing");
  const t = i18n.getFixedT(pageLanguage, "landing");
  const content = getCommunityContent(t, variant, pageLanguage);
  const desktopStartHref = "/events/search";
  const mobileStartHref = GET_APP_URL;
  const footerHref = variant === "organizer" ? `/host-guide/${pageLanguage}` : desktopStartHref;
  const footerCtaLabel = variant === "organizer" ? content.footerCtaLabel : content.desktopCtaLabel;
  const benefitsId = `${variant}-benefits`;
  const introStarSrc = variant === "participant"
    ? "/assets/one-pagers/participants/participant-star.svg"
    : "/assets/one-pagers/Star.svg";
  const englishUrl = `${PAGE_CONFIG[variant].pageUrl}/en`;
  const slovakUrl = `${PAGE_CONFIG[variant].pageUrl}/sk`;
  const czechUrl = `${PAGE_CONFIG[variant].pageUrl}/cs`;

  useEffect(() => {
    document.documentElement.classList.add("vg-community-landing-open");
    document.body.classList.add("vg-community-landing-open");

    return () => {
      document.documentElement.classList.remove("vg-community-landing-open");
      document.body.classList.remove("vg-community-landing-open");
    };
  }, []);

  return (
    <main className={`vg-community-landing vg-community-landing--${variant} vg-community-landing--lang-${pageLanguage}`}>
      <Helmet>
        <html lang={pageLanguage} />
        <title>{content.title}</title>
        <meta name="description" content={content.description} />
        <meta name="robots" content="index,follow,noimageindex" />
        <link rel="canonical" href={content.pageUrl} />
        <link rel="alternate" hrefLang="en" href={englishUrl} />
        <link rel="alternate" hrefLang="sk" href={slovakUrl} />
        <link rel="alternate" hrefLang="cs" href={czechUrl} />
        <link rel="alternate" hrefLang="x-default" href={englishUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="VaultGrid" />
        <meta property="og:title" content={content.title} />
        <meta property="og:description" content={content.description} />
        <meta property="og:url" content={content.pageUrl} />
        <meta property="og:image" content="https://vaultgrid.net/android-chrome-512x512.png" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      <div className="vg-community-landing__page">
        <section className="vg-community-landing__hero" aria-labelledby={`${variant}-hero-title`}>
          <div
            className="vg-community-landing__hero-image"
            style={{
              "--vg-community-hero-image": 'url("/assets/one-pagers/participants/participant-hero.png")',
              "--vg-community-mobile-hero-image": 'url("/assets/one-pagers/photo_vertical.png")',
            }}
            aria-hidden="true"
          />
          <div className="vg-community-landing__hero-overlay" aria-hidden="true" />
          <Brand variant={variant} />
          <div className="vg-community-landing__hero-copy">
            <h1 id={`${variant}-hero-title`}>
              <span>{content.heroTitle[0]}</span>
              <em>{content.heroTitle[1]}</em>
            </h1>
            <p>{content.heroLines.map((line) => <span key={line}>{line}</span>)}</p>
            <ResponsiveActionButton
              desktopHref={desktopStartHref}
              desktopLabel={content.desktopCtaLabel}
              mobileHref={mobileStartHref}
              mobileLabel={content.mobileCtaLabel}
            />
          </div>
          <PhoneMockup />
        </section>

        <StatsSection title={content.statsTitle} items={content.stats} />

        <section className="vg-community-landing__intro" aria-labelledby={`${variant}-intro-title`}>
          <div className="vg-community-landing__intro-heading">
            <h2 id={`${variant}-intro-title`}>
              {content.introTitle.map((line, index) => (
                <span key={line}>
                  {index === 0 && (
                    <img
                      className="vg-community-landing__inline-spark vg-community-landing__inline-spark--start"
                      src={introStarSrc}
                      alt=""
                      aria-hidden="true"
                    />
                  )}
                  {line}
                  {index === content.introTitle.length - 1 && (
                    <img
                      className="vg-community-landing__inline-spark vg-community-landing__inline-spark--end"
                      src={introStarSrc}
                      alt=""
                      aria-hidden="true"
                    />
                  )}
                </span>
              ))}
            </h2>
          </div>
          <p className="vg-community-landing__intro-body">
            <strong>VaultGrid</strong> {content.introBody.replace(/^VaultGrid\s*/, "")}
          </p>
          <a className="vg-community-landing__learn-more" href={`#${benefitsId}`}>
            <img src={variant === "participant" ? "/assets/one-pagers/participants/participant-arrow-down.svg" : "/assets/one-pagers/arrow_learn_more.svg"} alt="" aria-hidden="true" />
            <span>{content.learnMore}</span>
            <img src={variant === "participant" ? "/assets/one-pagers/participants/participant-arrow-down.svg" : "/assets/one-pagers/arrow_learn_more.svg"} alt="" aria-hidden="true" />
          </a>
        </section>

        <section id={benefitsId} className="vg-community-landing__benefits" aria-label="Benefits">
          {content.benefits.map((benefit, index) => (
            <BenefitRow key={benefit.title} index={index + 1} {...benefit} />
          ))}
        </section>

        {content.guided ? (
          <GuidedSection desktopHref={desktopStartHref} mobileHref={mobileStartHref} copy={content.guided} />
        ) : null}
        <Footer
          variant={variant}
          content={content}
          href={footerHref}
          ctaLabel={footerCtaLabel}
          mobileHref={variant === "participant" ? mobileStartHref : null}
          mobileCtaLabel={content.mobileCtaLabel}
        />
      </div>
    </main>
  );
}
